'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react';
import { RiInformationLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, ease, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { AD_INTEGRATIONS } from '@/lib/site-integrations';

import './campaigns.css';

/**
 * "Senkron saati yok" — canlı veri.
 *
 * Ritim ürünün kendisi (`@mentorivo/shared/live`): panel açıkken reklam verisi
 * 60 sn'de bir (Google 2 dk'da bir), kimse bakmazken 5 dk'da bir (Google
 * 15 dk); değişiklik ekrana itiliyor (SSE), yenile düğmesi yok.
 *
 * Gösterim bu ritmi HIZLANDIRIP oynatıyor: ~3 sn'de bir satır tazeleniyor,
 * değişen hücre yerinde kayarak yeni sayıya geçiyor, satırın "x sn önce"
 * sayacı sıfırlanıyor. Rakamlar örnek; not satırı hem bunu hem gerçek ritmi
 * söylüyor.
 *
 * Döngü: görüş alanı dışında ve azaltılmış hareket tercihinde DURUYOR (karar
 * efektte; ilk çizim sunucuyla aynı). "Canlı" noktasının nabzı da CSS'te
 * tercihe bağlı.
 */

type Row = { key: string; spend: number; conv: number; roas: number; ago: number };

const START: Row[] = [
  { key: 'meta', spend: 412.4, conv: 38, roas: 3.6, ago: 12 },
  { key: 'google', spend: 286.1, conv: 24, roas: 2.9, ago: 41 },
  { key: 'tiktok', spend: 154.8, conv: 9, roas: 1.7, ago: 5 },
  { key: 'linkedin', spend: 98.0, conv: 3, roas: 1.2, ago: 27 },
  { key: 'x', spend: 61.3, conv: 4, roas: 1.9, ago: 33 },
];

const KEYS = START.map((row) => row.key);
const TICK_MS = 3000;

export function CampaignLive() {
  const t = useTranslations('featuresCampaigns.live');
  const locale = useLocale();
  const uid = useId();
  const reduce = useReducedMotion();
  const tableRef = useRef<HTMLDivElement>(null);
  const inView = useInView(tableRef, { amount: 0.4 });
  const [rows, setRows] = useState<Row[]>(START);
  const [fresh, setFresh] = useState<string | null>(null);

  useEffect(() => {
    if (reduce || !inView) return;
    let turn = 0;
    const timer = window.setInterval(() => {
      turn += 1;
      /* Sırayla değil, ritmi andıran bir düzende: Google daha seyrek (gerçekte
         2 dk). Seçim güncelleyicinin DIŞINDA — güncelleyici saf kalmalı. */
      const pick = KEYS[(turn * 3) % KEYS.length]!;
      const key = pick === 'google' && turn % 2 === 0 ? 'meta' : pick;
      setFresh(key);
      setRows((current) =>
        current.map((row) => {
          if (row.key !== key) return { ...row, ago: row.ago + TICK_MS / 1000 };
          const add = Math.round((2 + ((turn * 7) % 9)) * 10) / 10;
          const conv = row.conv + (turn % 3 === 0 ? 1 : 0);
          return {
            ...row,
            spend: Math.round((row.spend + add) * 10) / 10,
            conv,
            roas: Math.round((row.roas + ((turn % 4) - 1.5) * 0.02) * 100) / 100,
            ago: 0,
          };
        }),
      );
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [reduce, inView]);

  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const ratio = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="cl" ref={tableRef}>
        <div className="cl-top">
          <p className="cl-title">{t('tableTitle')}</p>
          <span className="cl-live">
            <span className="cl-live-dot" aria-hidden="true" />
            {t('live')}
          </span>
        </div>

        <div className="cl-scroll">
          <table className="cl-table">
            <thead>
              <tr>
                <th scope="col">{t('cols.platform')}</th>
                <th scope="col" className="cl-num">
                  {t('cols.spend')}
                </th>
                <th scope="col" className="cl-num">
                  {t('cols.conv')}
                </th>
                <th scope="col" className="cl-num">
                  {t('cols.roas')}
                </th>
                <th scope="col" className="cl-num cl-ago-col">
                  {t('cols.updated')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const p = AD_INTEGRATIONS.find((item) => item.key === row.key)!;
                const isFresh = fresh === row.key && row.ago === 0;
                return (
                  <tr key={row.key} data-fresh={isFresh ? '' : undefined}>
                    <th scope="row">
                      <span className="cl-platform">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          className="cl-mark"
                          src={p.mark}
                          alt=""
                          width={p.w}
                          height={p.h}
                          loading="lazy"
                          decoding="async"
                        />
                        {p.name}
                      </span>
                    </th>
                    <td className="cl-num">
                      <Roll value={money.format(row.spend)} />
                    </td>
                    <td className="cl-num">
                      <Roll value={String(row.conv)} />
                    </td>
                    <td className="cl-num">
                      <Roll value={`${ratio.format(row.roas)}×`} />
                    </td>
                    <td className="cl-num cl-ago-col">
                      <span className="cl-ago">
                        {row.ago === 0 ? t('now') : t('ago', { seconds: row.ago })}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="ps-note">
        <RiInformationLine size={16} aria-hidden="true" />
        {t('note')}
      </p>

      <ul className="ps-facts">
        {(['watching', 'idle', 'push'] as const).map((key) => (
          <li key={key} className="ps-fact">
            <p className="ps-fact-title">{t(`facts.${key}.title`)}</p>
            <p className="ps-fact-text">{t(`facts.${key}.text`)}</p>
          </li>
        ))}
      </ul>
    </ProductSection>
  );
}

/**
 * Değişen sayı yerinde kayarak geçiyor: eskisi yukarı çıkıp sönüyor, yenisi
 * alttan geliyor (yalnız transform/opacity). Değer aynıysa hiçbir şey olmuyor —
 * anahtar değerin kendisi.
 */
function Roll({ value }: { value: string }) {
  return (
    <span className="cl-roll">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          className="cl-roll-value"
          initial={{ y: '70%', opacity: 0 }}
          animate={{ y: 0, opacity: 1, transition: springFade(spring.snap) }}
          exit={{ y: '-70%', opacity: 0, transition: { duration: dur.fast, ease: ease.in } }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
